import React from 'react';
import { useParams } from 'react-router-dom';
import { isOnboardingStep, type OnboardingStep } from './components/stepStrip';
import type { WizardColumnWidth } from './components/wizardFrame';
import { useOnboardingSession } from './useOnboardingSession';

/** Each step's column cap; a URL that names no known step gets Welcome's. */
export const STEP_COLUMN_WIDTH: Record<OnboardingStep, WizardColumnWidth> = {
    start: 960,
    accounting: 640,
    branch: 720,
    file: 1120,
    names: 1120,
    products: 'full',
    result: 960,
    done: 960,
};

const DEFAULT_COLUMN_WIDTH: WizardColumnWidth = 960;

/** The steps after Branch, which work on the chosen branch and name it in the frame. */
const BRANCH_CONTEXT_STEPS: ReadonlySet<OnboardingStep> = new Set([
    'file',
    'names',
    'products',
    'result',
]);

export type UseOnboardingPageResult = {
    /** The step the URL names; `null` for a segment that is not a step. */
    step: OnboardingStep | null;
    /** The bare `/onboarding` (the gate's target), which opens Start. */
    isBarePath: boolean;
    columnWidth: WizardColumnWidth;
    /** The frame names the chosen branch (Upload to Result). */
    showsBranchContext: boolean;
    /**
     * Accounting is a step of this visit: the organisation needs it, or
     * needed it when the wizard opened. Once the step has run the
     * organisation stops needing it, and the strip then shows it done, not
     * skipped.
     */
    accountingNeeded: boolean;
    /**
     * The year typed on Accounting, kept here so a trip to Welcome and back
     * finds it in the field (`null` while the field is empty). The current
     * year until the owner changes it; memory only, so a reload starts again.
     */
    accountingYear: number | null;
    onAccountingYearChange: (year: number | null) => void;
    organisationName: string;
    isSigningOut: boolean;
    onSignOut: () => void;
};

/**
 * The wizard's route state: the step is the first segment under
 * `/onboarding` (the route's splat), and the organisation decides whether
 * Accounting runs. That is remembered for the visit: one route element
 * serves every step, so the wizard stays mounted from Start to Done and a
 * reload starts the visit again.
 *
 * The visit also remembers the year typed on Accounting until it is
 * submitted (bos-app#201): Accounting's Back opens Welcome, and the step
 * opens again on that year. It lives in this hook's state, never in browser
 * storage, so a reload brings back the current year. Once accounting has
 * started the step never shows again, and the value is inert.
 */
export const useOnboardingPage = (): UseOnboardingPageResult => {
    const splat = useParams()['*'] ?? '';
    const segment = splat.split('/')[0];
    const step = isOnboardingStep(segment) ? segment : null;
    const session = useOnboardingSession();
    const neededNow = session.organisation?.accountingStartNeeded ?? false;
    const [neededThisVisit, setNeededThisVisit] = React.useState(neededNow);
    if (neededNow && !neededThisVisit) {
        setNeededThisVisit(true);
    }
    const [accountingYear, setAccountingYear] = React.useState<number | null>(
        () => new Date().getFullYear(),
    );

    return {
        step,
        isBarePath: segment === '',
        columnWidth: step ? STEP_COLUMN_WIDTH[step] : DEFAULT_COLUMN_WIDTH,
        showsBranchContext: step !== null && BRANCH_CONTEXT_STEPS.has(step),
        accountingNeeded: neededThisVisit,
        accountingYear,
        onAccountingYearChange: setAccountingYear,
        organisationName: session.organisationName,
        isSigningOut: session.isSigningOut,
        onSignOut: session.onSignOut,
    };
};
